import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import DropdownOption from './DropdownOption';
import { getClassNames, isMediaMatched } from '../../../utils';

const Dropdown = ({
  id, label, options, handleDropdownSelect,
}: DropdownProps) => {
  const [active, setActive] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const contentWrapper = useRef<HTMLDivElement>(null);
  const mediaQuery = isMediaMatched(1200);

  useEffect(() => {
    if (contentRef && contentRef.current && contentWrapper && contentWrapper.current) {
      if (!mediaQuery) {
        contentRef.current.style.maxHeight = active ? `${contentRef.current.scrollHeight}px` : '0px';
        contentWrapper.current.classList.add('has-expandable-open');
      } else {
        const maxHeightLimit = window.innerHeight - 176;

        const maxHeight = contentRef.current.scrollHeight < maxHeightLimit
          ? contentRef.current.scrollHeight
          : maxHeightLimit;

        contentRef.current.style.maxHeight = `${maxHeight}px`;
        contentWrapper.current.classList.remove('has-expandable-open');
      }
    }
  }, [contentRef, active, contentWrapper, mediaQuery]);

  const handleToggle = () => {
    setActive(!active);
  };

  return (
    <div
      className={getClassNames(null, 'c-expandable c-expandable--cyop', {
        'is-open': active,
      })}
      onClick={handleToggle}
      onKeyDown={handleToggle}
      ref={contentWrapper}
      role="button"
      tabIndex={0}
    >
      <button type="button" className="c-expandable__header" id={id}>
        <p className="c-expandable__title">{label}</p>
        <span className={active ? 'c-expandable__arrow  is-rotate' : 'c-expandable__arrow'}>
          <svg>
            <use xlinkHref="#chevron-down" />
          </svg>
        </span>
      </button>

      <div className="c-expandable__content" ref={contentRef}>
        {options.map((option) => {
          const { label: optionLabel, value } = option;

          if (parseInt(value, 10) === 0) return false;

          return (
            <DropdownOption
              isCurrent={label === optionLabel}
              handleSelect={handleDropdownSelect}
              key={value}
              label={optionLabel}
              value={value}
            />
          );
        })}
      </div>
    </div>
  );
};

const dropdownPropTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired).isRequired,
  handleDropdownSelect: PropTypes.func.isRequired,
};

Dropdown.propTypes = dropdownPropTypes;

type DropdownProps = PropTypes.InferProps<typeof dropdownPropTypes>;

export default Dropdown;
